"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, FileText, Sparkles, Tag } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Guide() {
  const highlights = [{
    id: "hl-1",
    text: "Démarches administratives : Personnummer, Skatteverket, compte bancaire. Le tout, expliqué pas à pas"
  }, {
    id: "hl-2",
    text: "Trouver un logement à Stockholm et dans les grandes villes, même sans être sur place. "
  }, {
    id: "hl-3",
    text: "Budget réel, vie quotidienne, santé et intégration dans la société suédoise"
  }];
  return <section id="heroguide" data-nav="light" className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Decorative notebook paper subtle grid styling */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(#2A2825_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Column 1: Book cover illustration */}
          <div className="flex justify-center items-center w-full">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/Couverture-de-livre-numerique-4-2.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/Couverture-de-livre-numerique-4-2.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/Couverture-de-livre-numerique-4-2.png-wvc-sizes" alt="Couverture du guide S'installer en Suède : Le mode d'emploi" loading="lazy" className="w-full max-w-md lg:max-w-none h-auto object-contain rounded-lg shadow-md mx-auto" />
          </div>

          {/* Column 2: Notebook-styled container card */}
          <div className="relative rounded-2xl bg-card border border-border shadow-lg p-6 sm:p-10 md:p-12">
            {/* Top category / badge */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-8 border-b border-border">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                  <Sparkles className="size-3.5 mr-1" />
                  Guide numérique pratique
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">Édition 2026</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <BookOpen className="size-4 text-secondary" />
                <span>Format PDF · 65 pages</span>
              </div>
            </div>

            {/* Main Title */}
            <div className="space-y-6 text-left">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-foreground leading-[1.15]">
                S'installer en Suède : Le mode d'emploi
              </h1>

              {/* Subtitle / Personal Note */}
              <div className="relative pl-5 sm:pl-6 border-l-2 border-secondary/60 my-6">
                <p className="text-base sm:text-lg md:text-xl text-foreground/90 font-default leading-relaxed italic"> « Quand on a préparé notre départ, on a passé des heures à chercher des infos fiables. Une page Skatteverket traduite de travers, des forums qui se contredisent d'un message à l'autre, et un carnet perso raturé de bout en bout: on a passé quelques soirées à s'arracher les cheveux. Ce guide, c'est celui qu'on aurait aimé avoir avant de partir. »</p>
              </div>

              {/* Key takeaway checklist */}
              <div className="pt-2 pb-4 space-y-2.5">
                {highlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-2.5 text-sm sm:text-base text-foreground/85">
                    <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span>{item.text}</span>
                  </div>)}
              </div>

              {/* Pricing & CTA area */}
              <div className="pt-6 mt-6 border-t border-border space-y-4">
                {/* Promo code badge / encart */}
                <div className="flex flex-wrap items-center gap-2.5">
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs font-semibold tracking-wide font-mono flex items-center gap-1.5">
                    <Tag className="size-3 text-secondary-foreground" />
                    <span>OCTOBER15 (-15%)</span>
                  </Badge>
                  <span className="text-xs text-muted-foreground font-medium">
                    jusqu'au 31 octobre 2026
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-serif text-4xl sm:text-5xl font-bold text-foreground tracking-tight">
                      33,15€
                    </span>
                    <span className="text-2xl sm:text-3xl font-serif text-muted-foreground line-through decoration-secondary/80">
                      39€
                    </span>
                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        Accès immédiat
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch sm:items-end">
                    <Button asChild variant="secondary" size="lg" className="rounded-xl px-8 py-6 text-base font-medium shadow-md hover:shadow-lg transition-[color,background-color,box-shadow]">
                      <Link to="https://payhip.com/b/xB6sv" target="_blank" rel="noopener noreferrer">
                        <span>Je veux le guide</span>
                        <ArrowRight className="size-5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Reassurance notes */}
              <div className="flex flex-wrap items-center gap-4 pt-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <FileText className="size-3.5 text-secondary" />
                  <span>Lecture sur mobile, tablette et liseuse</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>Paiement sécurisé via Payhip</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}